import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { LicenseSkillAssignment } from '../types';
import { generateLicenseKey } from '../utils/license-utils';
import SkillSelectionPanel from '@/features/license-keys/components/SkillSelectionPanel';

interface LicenseCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onCreate: (payload: {
    key: string;
    max_activations: number;
    expires_at?: string;
    notes?: string;
  }) => Promise<string>;
  onAssociateSkills: (licenseKey: string, skillIds: string[]) => Promise<void>;
  availableSkills: LicenseSkillAssignment[];
  selectedSkills: string[];
  loadingSkills: boolean;
  onToggleSkill: (skillId: string, checked: boolean) => void;
  onRefresh: () => void;
}

/**
 * 单个许可证创建对话框
 */
export default function LicenseCreateDialog({
  open,
  onClose,
  onSuccess,
  onError,
  onCreate,
  onAssociateSkills,
  availableSkills,
  selectedSkills,
  loadingSkills,
  onToggleSkill,
  onRefresh,
}: LicenseCreateDialogProps) {
  const { t } = useTranslation();

  const [newKey, setNewKey] = useState('');
  const [maxActivations, setMaxActivations] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [batchPrefix, setBatchPrefix] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 对话框打开时重置表单
  useEffect(() => {
    if (open) {
      setNewKey('');
      setMaxActivations(1);
      setExpiresAt('');
      setNotes('');
      setBatchPrefix('');
      setError('');
    }
  }, [open]);

  const handleGenerateKey = () => {
    setNewKey(generateLicenseKey(batchPrefix));
  };

  const handleSubmit = async () => {
    setError('');

    if (!newKey.trim()) {
      setError(t('licenses.licenseKeyRequired'));
      return;
    }

    const payload = {
      key: newKey.trim(),
      max_activations: maxActivations,
      ...(expiresAt && { expires_at: expiresAt }),
      ...(notes && { notes }),
    };

    setLoading(true);
    try {
      const createdKey = await onCreate(payload);

      if (selectedSkills.length > 0) {
        try {
          await onAssociateSkills(createdKey, selectedSkills);
        } catch (skillsError) {
          console.error('Failed to associate skills with created license:', skillsError);
        }
      }

      onSuccess(t('licenses.createdSuccess'));
      onClose();
      onRefresh();
    } catch (requestError: any) {
      const errorMessage = requestError.response?.data?.error || requestError.message || t('licenses.createFailed');
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('licenses.createTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              label={t('licenses.licenseKey')}
              value={newKey}
              onChange={(event) => setNewKey(event.target.value)}
              placeholder={t('licenses.licenseKeyPlaceholder')}
            />
            <TextField
              label={t('licenses.prefix')}
              value={batchPrefix}
              onChange={(event) => setBatchPrefix(event.target.value.toUpperCase())}
              inputProps={{ maxLength: 3 }}
              sx={{ width: 100 }}
            />
            <Button variant="outlined" onClick={handleGenerateKey}>
              {t('licenses.generate')}
            </Button>
          </Box>

          <TextField
            fullWidth
            type="number"
            label={t('licenses.maxActivations')}
            value={maxActivations}
            onChange={(event) => setMaxActivations(parseInt(event.target.value, 10) || 1)}
            inputProps={{ min: 1 }}
          />

          <TextField
            fullWidth
            type="date"
            label={t('licenses.expirationDate')}
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={t('licenses.expirationDateHint')}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('licenses.notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />

          <Divider sx={{ my: 1 }} />

          <SkillSelectionPanel
            title="Enabled Skills"
            description="Select which skills this license can use."
            skills={availableSkills}
            selectedSkillIds={selectedSkills}
            loading={loadingSkills}
            onToggleSkill={onToggleSkill}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('licenses.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {t('licenses.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
